import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ArrowSmRightIcon } from "@heroicons/react/outline";
import * as Sentry from "@sentry/browser";
import { Formik } from "formik";
import { axiosErr } from "@hilos/types/axios";
import { ActiveCampaignAppConfig } from "@hilos/types/private-schema";
import SecretInputField from "src/components/Form/SecretInputField";
import TextInputField from "src/components/Form/TextInputField";
import StateButton from "src/components/StateButton";
import APIErrors from "src/components/base/APIErrors";
import useCleanTimeout from "src/hooks/useCleanTimeout";
import * as meta from "./Meta";

interface ActiveCampaignAppConfigFormProps {
  currentSettings: ActiveCampaignAppConfig;
  disabled: boolean;
  onUpdateActiveCampaignAppConfig;
}

export default function ActiveCampaignAppConfigForm({
  currentSettings,
  disabled,
  onUpdateActiveCampaignAppConfig,
}: ActiveCampaignAppConfigFormProps) {
  const { t } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timeout = useCleanTimeout();
  const [backendError, setBackendError] = useState("");
  const [backendValidationErrors, setBackendValidationErrors] = useState({});

  async function formSubmit(
    config: ActiveCampaignAppConfig,
    setSubmitting: (boolean) => void
  ) {
    setBackendValidationErrors({});
    setBackendError("");
    try {
      setSubmitting(true);
      await onUpdateActiveCampaignAppConfig(config);
      setSuccess(true);
    } catch (e) {
      Sentry.captureException(e);
      setSuccess(false);
      const err = e as axiosErr;
      if (err?.response?.status === 400) {
        console.error("ActiveCampaignAppConfig", err);
        setBackendValidationErrors(err.response.data);
      } else {
        setBackendError(
          t(
            "integrations:activecampaign.settings.error-save-text",
            "An error occured, please try again."
          )
        );
      }
    } finally {
      setSubmitting(false);
      setSubmitted(true);
      timeout.current = setTimeout(() => {
        setSubmitted(false);
      }, 1000 * 2);
    }
  }

  return (
    <Formik
      onSubmit={(values: ActiveCampaignAppConfig, { setSubmitting }) =>
        formSubmit(values, setSubmitting)
      }
      enableReinitialize={true}
      initialValues={currentSettings}
    >
      {(formik) => (
        <form noValidate onSubmit={formik.handleSubmit}>
          <div
            className={`mt-10 sm:mt-4 ${
              disabled && "opacity-50 hover:cursor-not-allowed"
            }`}
          >
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <div className="px-4 sm:px-0">
                  <Trans i18nKey="integrations:activecampaign.settings.description">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      ActiveCampaign Settings
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Set your API key to connect your Hilos account to
                      ActiveCampaign.
                    </p>
                  </Trans>
                </div>
              </div>
              <div className="mt-5 md:col-span-2 md:mt-0">
                <div className="shadow sm:overflow-hidden sm:rounded-md">
                  <div className="space-y-6 bg-white px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-3 gap-6">
                      <div className="col-span-3 space-y-4">
                        <APIErrors
                          APIError={backendError}
                          APIValidationErrors={backendValidationErrors}
                          fieldTranslations={meta.FIELDS}
                        />
                        <TextInputField
                          label={t(meta.FIELDS.base_url.label)}
                          name={meta.FIELDS.base_url.key}
                          type="text"
                        />
                        <SecretInputField
                          readOnlyName={meta.FIELDS.api_key_ro.key}
                          label={t(meta.FIELDS.api_key.label)}
                          name={meta.FIELDS.api_key.key}
                          help={meta.FIELDS.api_key.help}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <div
              className={`text-right ${
                disabled && "opacity-50 hover:cursor-not-allowed"
              }`}
            >
              <StateButton
                isSubmitting={formik.isSubmitting}
                submitted={submitted}
                success={success}
                disabled={
                  disabled ||
                  (formik.errors &&
                    Object.getOwnPropertyNames(formik.errors).length > 0)
                }
                successText={t(
                  "integrations:activecampaign.settings.success-text",
                  "ActiveCampaign settings saved!"
                )}
                initialText={
                  <>
                    {t("save")}
                    <ArrowSmRightIcon
                      className="ml-1 h-5 w-5"
                      aria-hidden="true"
                    />
                  </>
                }
              />
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}
